import React, { useState, useEffect } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { useSAPDataStore } from "../store/SAPDataStore";
import { useDashboardTabsStore } from "../store/DashboardTabsStore";
import type { Machine } from "../services/BackendDataService";
import Overview from "../components/Overview";
import Preset from "../components/Preset";
import ConditionMonitoring from "../components/ConditionMonitoring";
import Dashboard from "../components/Dashboard";
import LiveShift from "../components/LiveShift";
import { getTabIcon } from "../components/tabIcons";
import SortableTabBar from "../components/SortableTabBar";

const FIXED_MACHINE_TAB_IDS = [
  "overview",
  "preset",
  "condition-monitoring",
  "live-shift",
];
import { styles, cx } from "../styles";
import { useTheme } from "../providers/ThemeProvider";
import { useLiveShiftData } from "../hooks/useLiveShiftData";

interface MachineViewProps {
  machineId?: string;
  machineName?: string;
}

// Tab type
interface Tab {
  id: string;
  label: string;
}

// Helper function to get initial tabs based on machine data
const getInitialTabs = (machineData: any): Tab[] => {
  const baseTabs: Tab[] = [
    { id: "overview", label: "Overview" },
    { id: "preset", label: "Preset" },
  ];

  // Add Condition Monitoring tab if CM data is available
  if (machineData?.cmVerfuegbar) {
    baseTabs.push({
      id: "condition-monitoring",
      label: "Condition Monitoring",
    });
  }

  return baseTabs;
};

const MachineView = ({
  machineId: propMachineId,
  machineName: propMachineName,
}: MachineViewProps = {}) => {
  const { recordVisit, currentUser, getMachineById, getCachedMachineById } =
    useSAPDataStore();
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  // Reactive store flags used to decide whether the machine list has genuinely
  // finished loading (so we can tell "still loading" apart from "loaded but the
  // user has no access to this machine").
  const sapLoading = useSAPDataStore((s) => s.loading);
  const sapLastFetched = useSAPDataStore((s) => s.lastFetched);
  const sapError = useSAPDataStore((s) => s.error);
  const machinesCount = useSAPDataStore((s) => s.machines.length);
  const {
    hasHydrated,
    initializeMachineTabState,
    getMachineTabState,
    setActiveTab,
    addTab,
    removeTab,
    moveTab,
    setTabs,
  } = useDashboardTabsStore();

  const { machineId: routeMachineId } = useParams<{ machineId: string }>();
  const location = useLocation();
  const state =
    (location.state as {
      machineId?: string;
      machineName?: string;
      lineId?: string;
      machineType?: string;
      factory?: string;
    }) || {};

  // Determine machine ID and name from various sources
  const machineId = routeMachineId || propMachineId || state.machineId;

  // Try to get machine data from SAP store first, then cached navigation history
  const machineData = machineId
    ? getMachineById(machineId) || getCachedMachineById(machineId)
    : null;

  // Access detection: if the SAP machine list has finished loading and this
  // machine is neither in that list nor in the user's navigation cache (which
  // would imply prior access), the user most likely has no access right to it.
  // We require a completed fetch (lastFetched set, not loading, no error) with a
  // non-empty list so we never flash this while data is still loading.
  const sapListReady =
    !sapLoading && !!sapLastFetched && !sapError && machinesCount > 0;
  const noAccessToMachine =
    hasHydrated && !!machineId && sapListReady && !machineData;

  // Check if edge-data stream exists for this machine
  const {
    hasStream: hasEdgeDataStream,
    shiftData,
    lastTransition,
    topStops,
    topRejects,
    loading: liveShiftLoading,
  } = useLiveShiftData(machineId);

  // Determine machine name from various sources
  const machineName =
    machineData?.machineName ||
    machineData?.bautyp ||
    propMachineName ||
    state.machineName ||
    (machineId ? `Machine ${machineId}` : "Unknown Machine");

  // Record the visit when component mounts or machine changes - but only when
  // user is ready AND the user actually has access (don't pollute history /
  // the machine cache with machines the user can't access).
  useEffect(() => {
    if (noAccessToMachine) return;
    if (machineId && machineName && currentUser) {
      console.log(
        `🏭 MachineView: Recording visit for "${machineName}" (${machineId}) - User: ${currentUser}`,
      );
      recordVisit("machine", machineId, machineName);
    } else {
      console.warn(
        `🏭 MachineView: Cannot record visit - machineId: ${machineId}, machineName: ${machineName}, currentUser: ${currentUser}`,
      );
    }
  }, [machineId, machineName, currentUser, noAccessToMachine]);

  // Initialize machine tab state when store is hydrated.
  // NOTE: we intentionally do NOT gate this on `machineData` being present.
  // On a deep link / refresh, or for a machine not yet in the SAP store/cache,
  // `machineData` can be null for a while (or indefinitely). Gating init on it
  // left the tab bar empty (only the "+" button showed). getInitialTabs handles
  // a null machine safely (Overview + Preset always; CM added later when data
  // arrives via the effect below).
  useEffect(() => {
    if (hasHydrated && machineId) {
      const initialTabs = getInitialTabs(machineData);
      initializeMachineTabState(machineId, initialTabs, "overview");
    }
  }, [hasHydrated, machineId, machineData, initializeMachineTabState]);

  // Get current machine tab state
  const machineTabState =
    hasHydrated && machineId ? getMachineTabState(machineId) : null;
  const tabs = machineTabState?.tabs || [];
  const activeTabId = machineTabState?.activeTabId || "";

  // Add or remove Live Shift tab based on edge-data stream availability.
  // `hasEdgeDataStream` arrives asynchronously from the worker (STREAM_CHECK),
  // often AFTER tabs are initialized, so this effect must reliably re-run when
  // it flips. We read the latest tab state straight from the store (rather than
  // the possibly-stale `machineTabState` closure) and key the effect on the
  // primitive `hasEdgeDataStream` (+ tab count) so a late stream check still
  // adds the tab.
  useEffect(() => {
    if (!hasHydrated || !machineId) return;

    const current = getMachineTabState(machineId);
    if (!current) return; // tab state not initialized yet; init effect runs first

    const hasLiveShiftTab = current.tabs.some((tab) => tab.id === "live-shift");

    if (hasEdgeDataStream && !hasLiveShiftTab) {
      // Insert Live Shift tab at the beginning (before overview)
      const newTabs = [
        { id: "live-shift", label: "Live Shift" },
        ...current.tabs,
      ];
      setTabs(machineId, newTabs);
      // Set live-shift as active only if the user is still on the default tab
      if (current.activeTabId === "overview" || current.activeTabId === "") {
        setActiveTab(machineId, "live-shift");
      }
    } else if (!hasEdgeDataStream && hasLiveShiftTab) {
      // Remove Live Shift tab
      const newTabs = current.tabs.filter((tab) => tab.id !== "live-shift");
      setTabs(machineId, newTabs);
      // If live-shift was active, fall back to overview
      if (current.activeTabId === "live-shift") {
        setActiveTab(machineId, "overview");
      }
    }
  }, [
    hasHydrated,
    machineId,
    hasEdgeDataStream,
    tabs.length,
    getMachineTabState,
    setTabs,
    setActiveTab,
  ]);

  // Update tabs when machine data changes (e.g., when loading completes)
  useEffect(() => {
    if (!(hasHydrated && machineId && machineData)) return;

    const current = getMachineTabState(machineId);
    if (!current) return;

    // Check if CM availability has changed
    const currentHasConditionMonitoring = current.tabs.some(
      (tab) => tab.id === "condition-monitoring",
    );
    const shouldHaveConditionMonitoring = !!machineData?.cmVerfuegbar;

    console.log(
      `🔧 MachineView [${machineId}]: cmVerfuegbar=${machineData?.cmVerfuegbar} (raw), shouldHaveCM=${shouldHaveConditionMonitoring}, currentHasCM=${currentHasConditionMonitoring}`,
    );

    if (currentHasConditionMonitoring !== shouldHaveConditionMonitoring) {
      const baseTabs = getInitialTabs(machineData);

      // Preserve the Live Shift tab (added by the stream-availability effect)
      // and keep it leading, exactly as that effect places it. Without this,
      // a CM toggle after data load would drop an existing Live Shift tab.
      const hasLiveShiftTab = current.tabs.some(
        (tab) => tab.id === "live-shift",
      );
      const leadingTabs = hasLiveShiftTab
        ? [{ id: "live-shift", label: "Live Shift" }]
        : [];

      // Keep any custom tabs that were added by the user
      const customTabs = current.tabs.filter(
        (tab) =>
          ![
            "overview",
            "preset",
            "condition-monitoring",
            "live-shift",
          ].includes(tab.id),
      );
      setTabs(machineId, [...leadingTabs, ...baseTabs, ...customTabs]);
    }
  }, [
    hasHydrated,
    machineId,
    machineData,
    machineData?.cmVerfuegbar,
    getMachineTabState,
    setTabs,
  ]);

  // Show loading state while store is hydrating
  if (!hasHydrated) {
    return (
      <div className={styles.home.container}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // The machine list has loaded but this machine isn't in it (and isn't in the
  // user's history cache): most likely the user has no access right to it.
  if (noAccessToMachine) {
    return (
      <div className={styles.home.container}>
        <div className="flex flex-col items-center justify-center h-64 text-center px-6">
          <div className="text-5xl mb-4">🔒</div>
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: "var(--text-primary)" }}
          >
            {t("noAccessToMachineTitle") || "No access to this machine"}
          </h2>
          <p
            className="text-sm max-w-md"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("noAccessToMachineMessage") ||
              `It looks like you don't have access rights to machine ${machineId}, or it doesn't exist. Please contact your administrator if you believe this is an error.`}
          </p>
        </div>
      </div>
    );
  }

  // Modal for new tab name
  function AddTabModal({
    isOpen,
    onClose,
    onSubmit,
    defaultName,
    existingNames,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (name: string) => void;
    defaultName: string;
    existingNames: string[];
  }) {
    const [name, setName] = useState(defaultName);
    const [error, setError] = useState("");

    useEffect(() => {
      setName(defaultName);
      setError("");
    }, [isOpen, defaultName]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError("Name cannot be empty.");
        return;
      }
      if (existingNames.includes(trimmed)) {
        setError("Name must be unique.");
        return;
      }
      onSubmit(trimmed);
      onClose();
    };

    if (!isOpen) return null;
    return (
      <div className={styles.modal.overlay}>
        <div
          className={styles.modal.container}
          style={{
            backgroundColor: "var(--bg-secondary)",
            borderColor: "var(--border-primary)",
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}
        >
          <h2
            className={styles.modal.header.title}
            style={{
              color: "var(--text-primary)",
              transition: "color 0.3s ease",
            }}
          >
            Add New Dashboard Tab
          </h2>
          <form onSubmit={handleSubmit}>
            <label
              className={styles.modal.form.label}
              style={{
                color: "var(--text-primary)",
                transition: "color 0.3s ease",
              }}
            >
              Tab Name
            </label>
            <input
              className={styles.modal.form.input}
              style={{
                backgroundColor: "var(--bg-primary)",
                borderColor: "var(--border-primary)",
                color: "var(--text-primary)",
                transition:
                  "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
              }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={40}
            />
            {error && <div className={styles.modal.form.error}>{error}</div>}
            <div className={styles.modal.actions.container}>
              <button
                type="button"
                onClick={onClose}
                className={styles.modal.actions.cancel}
                style={{
                  backgroundColor: "var(--bg-primary)",
                  color: "var(--text-secondary)",
                  transition: "background-color 0.3s ease, color 0.3s ease",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.modal.actions.submit}
                style={{
                  backgroundColor: "#2563eb", // blue-600
                  color: "white",
                  transition: "background-color 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#1d4ed8")
                } // blue-700
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#2563eb")
                } // blue-600
              >
                Add
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Helper to get next available dashboard number
  function getNextDashboardNumber(existingTabs: { label: string }[]): number {
    let n = 1;
    const usedNumbers = new Set<number>();
    existingTabs.forEach((tab) => {
      const match = tab.label.match(/^My Dashboard (\d+)$/);
      if (match) {
        usedNumbers.add(Number(match[1]));
      }
    });
    while (usedNumbers.has(n)) n++;
    return n;
  }

  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingDefaultName, setPendingDefaultName] = useState("");

  const handleAddTab = () => {
    const nextNumber = getNextDashboardNumber(tabs);
    let defaultName = `My Dashboard ${nextNumber}`;
    setPendingDefaultName(defaultName);
    setShowAddModal(true);
  };

  const handleAddTabModalSubmit = (name: string) => {
    if (!machineId) return;

    // Ensure uniqueness (should be handled in modal, but double check)
    let baseName = name;
    let suffix = 2;
    let finalName = name;
    while (tabs.some((tab) => tab.label === finalName)) {
      finalName = `${baseName} (${suffix++})`;
    }
    const newTabId = `tab-${Date.now()}`;
    const newTab = { id: newTabId, label: finalName };
    addTab(machineId, newTab);
  };

  const handleRemoveTab = (id: string) => {
    if (!machineId) return;
    removeTab(machineId, id);
  };

  const handleTabClick = (id: string) => {
    if (!machineId || id === activeTabId) return;
    setActiveTab(machineId, id);
    // Animate the content drop only on an actual user click.
    contentControls.start({
      opacity: [0, 1],
      y: [-14, 0],
      scale: [0.985, 1],
      transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
    });
  };

  // Reorder tabs (called by the shared tab bar's drag-and-drop).
  const handleReorder = (activeId: string, overId: string) => {
    if (!machineId) return;
    const newIndex = tabs.findIndex((tab) => tab.id === overId);
    if (newIndex !== -1) moveTab(machineId, activeId, newIndex);
  };

  // Add state for Overview tab
  const [overviewShifts, setOverviewShifts] = useState<string[]>(["A"]);
  const [overviewActiveTab, setOverviewActiveTab] = useState("Efficiency");

  // Content "drop" animation — triggered only by a user tab click (see
  // handleTabClick), never on page load / refresh or async state changes.
  const contentControls = useAnimationControls();

  return (
    <div className={styles.home.container}>
      {/* Shared tab bar (pill on desktop, bottom nav on mobile) */}
      <SortableTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        fixedTabIds={FIXED_MACHINE_TAB_IDS}
        onTabClick={handleTabClick}
        onReorder={handleReorder}
        onAddTab={handleAddTab}
        onRemoveTab={handleRemoveTab}
        getIcon={getTabIcon}
      />

      {/* Content area — extra bottom padding on mobile clears the fixed nav.
          Animates in like a drop from the selected tab on every switch. */}
      <motion.div
        animate={contentControls}
        className={cx(styles.sortableTabs.content.container, "pb-24 sm:pb-4")}
        style={{
          transformOrigin: "top center",
          backgroundColor: currentTheme.isDark
            ? "var(--bg-secondary)"
            : "#f3f4f6",
        }}
      >
        {activeTabId === "live-shift" ? (
          <LiveShift
            machineId={machineId}
            machineName={machineName}
            shiftData={shiftData}
            lastTransition={lastTransition}
            topStops={topStops}
            topRejects={topRejects}
            loading={liveShiftLoading}
          />
        ) : activeTabId === "overview" ? (
          <Overview
            machineId={machineId}
            machineName={machineName}
            machineData={machineData}
            shifts={overviewShifts}
            setShifts={setOverviewShifts}
            activeTab={overviewActiveTab}
            setActiveTab={setOverviewActiveTab}
          />
        ) : activeTabId === "preset" ? (
          <Preset
            machineId={machineId}
            machineName={machineName}
            machineData={machineData as Machine | null | undefined}
          />
        ) : activeTabId === "condition-monitoring" ? (
          <ConditionMonitoring
            machineId={machineId}
            machineName={machineName}
            machineData={machineData as Machine | null | undefined}
          />
        ) : (
          <Dashboard
            key={activeTabId}
            machineId={machineId}
            machineName={machineName}
            dashboardId={activeTabId}
            dashboardName={
              tabs.find((tab) => tab.id === activeTabId)?.label || "Dashboard"
            }
          />
        )}
      </motion.div>
      <AddTabModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddTabModalSubmit}
        defaultName={pendingDefaultName}
        existingNames={tabs.map((tab) => tab.label)}
      />
    </div>
  );
};

export default MachineView;
